// Em que pé está o boleto de cada parcela de acordo (#6540). Regra pura, usada pelo
// cronograma do acordo e pela aba "Emitir e enviar" de Parcelamentos.
//
// Antes, o cronograma mostrava "Faturado" tanto para a parcela que só tinha a
// fatura calculada quanto para a que já tinha boleto enviado ao associado, e a
// equipe não sabia o que faltava fazer sem abrir a fatura em Faturas.
//
// O sistema continua sem emitir sozinho (decisão de 09/10/2026, #6205): a regra só
// diz a etapa e qual tela abre — a mesma das faturas mensais.

import { selecionarParaEmitir, type LinhaParaEmitir } from './parcelasParaEmitir.ts';

export type EtapaBoleto =
  | 'cancelada'        // parcela cancelada, ou a fatura dela foi excluída
  | 'paga'
  | 'sem_fatura'       // ainda não virou fatura (acordo que não vale, ou espera a fatura do mês)
  | 'no_boleto_mensal' // somada no boleto de uma fatura mensal: segue o envio do mês
  | 'sem_boleto'       // fatura pronta, boleto ainda não emitido
  | 'a_enviar'         // boleto emitido, ainda não enviado ao associado
  | 'enviada'          // enviado, em aberto
  | 'vencida'          // enviado e vencido sem pagamento
  | 'em_preparo';      // fatura em outra etapa (processamento, validação)

/** Tela que o botão da parcela abre: a mesma do quadro de Faturas para aquele passo. */
export type AcaoBoleto = 'emitir' | 'enviar' | 'abrir' | null;

export interface DadosFaturaParcela {
  status_fatura?: string | null;
  num_cobranca_boleto?: string | null;
  status_boleto_vigor?: string | null;
  vencimento_boleto?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
  parcelamento_origem_id?: string | null;
}

export interface DadosParcela {
  status?: string | null;
  data_vencimento?: string | null;
  fatura_id?: number | string | null;
}

export const ROTULO_ETAPA: Record<EtapaBoleto, string> = {
  cancelada: 'Cancelada',
  paga: 'Paga',
  sem_fatura: 'Sem fatura',
  no_boleto_mensal: 'No boleto mensal',
  sem_boleto: 'Boleto não emitido',
  a_enviar: 'Emitido, falta enviar',
  enviada: 'Enviada',
  vencida: 'Vencida sem pagamento',
  em_preparo: 'Fatura em preparo',
};

export const ACAO_ETAPA: Record<EtapaBoleto, AcaoBoleto> = {
  cancelada: null,
  paga: null,
  sem_fatura: null,
  no_boleto_mensal: null,
  sem_boleto: 'emitir',
  a_enviar: 'enviar',
  enviada: 'abrir',
  vencida: 'abrir',
  em_preparo: null,
};

/** Status da fatura que cada tela de Faturas atende — é por eles que o botão abre. */
export const STATUS_DA_ACAO: Record<Exclude<AcaoBoleto, null>, string> = {
  emitir: 'Com boleto calculado',
  enviar: 'Com boleto emitido / A enviar',
  abrir: 'Cobrança enviada',
};

const PAGO = /^(pago|quitado)/i;
const CANCELADO = /^cancelad/i;

export function etapaBoletoParcela(
  parcela: DadosParcela,
  fatura: DadosFaturaParcela | null | undefined,
  acordoId: string,
  hoje: string, // yyyy-mm-dd (Brasília)
): EtapaBoleto {
  const statusParcela = String(parcela.status || '').toLowerCase();
  if (statusParcela === 'cancelado') return 'cancelada';
  const statusBoleto = String(fatura?.status_boleto_vigor || '').trim();
  // Pago vale antes de o status mudar: a sincronização do Sicoob grava a data de
  // pagamento primeiro (mesma regra da página pública do acordo).
  if (statusParcela === 'pago' || PAGO.test(statusBoleto) || fatura?.data_pgto_boleto || fatura?.quitado_em) return 'paga';
  if (!parcela.fatura_id || !fatura) return 'sem_fatura';
  // A fatura ligada é a mensal da UC (parcela somada no boleto do mês): o boleto é
  // o da fatura do mês, que sai pelo envio de sempre.
  if (fatura.parcelamento_origem_id !== acordoId) return 'no_boleto_mensal';
  const statusFatura = String(fatura.status_fatura || '');
  if (statusFatura === 'Excluído' || CANCELADO.test(statusFatura)) return 'cancelada';
  const temBoleto = !!fatura.num_cobranca_boleto && !CANCELADO.test(statusBoleto);
  if (temBoleto && statusFatura === STATUS_DA_ACAO.enviar) return 'a_enviar';
  if (temBoleto && statusFatura === STATUS_DA_ACAO.abrir) {
    const vence = String(fatura.vencimento_boleto || parcela.data_vencimento || '').slice(0, 10);
    return /^vencido/i.test(statusBoleto) || (vence && vence < hoje) ? 'vencida' : 'enviada';
  }
  if (!temBoleto && statusFatura === STATUS_DA_ACAO.emitir) return 'sem_boleto';
  return 'em_preparo';
}

// ── Aba "Emitir e enviar" ──────────────────────────────────────────────────────

/** Até quantos dias antes do vencimento a parcela enviada entra em "Vencem em breve". */
export const DIAS_LEMBRAR = 10;
const ACORDO_VALE = new Set(['ativo', 'em_atraso']);

export type GrupoAcompanhamento = 'emitir' | 'enviar' | 'lembrar' | 'vencidas';

export interface LinhaAcompanhamento extends LinhaParaEmitir {
  etapa: EtapaBoleto;
  /** Mês da parcela: é por UC + mês que o atalho abre a fatura em Faturas. */
  mes_referencia: string | null;
}

type LinhaBanco = Parameters<typeof selecionarParaEmitir>[0][number] & {
  mes_referencia?: string | null;
  fatura?: (DadosFaturaParcela & { num_cobranca_boleto?: string | null }) | null;
};

/** Dias de `de` até `ate` (yyyy-mm-dd). Negativo = `ate` já passou. */
export const diasEntre = (de: string, ate: string) =>
  Math.round((Date.parse(`${ate}T00:00:00Z`) - Date.parse(`${de}T00:00:00Z`)) / 86_400_000);

/**
 * Separa as parcelas dos acordos que valem no que a equipe tem a fazer:
 *  - emitir: a mesma regra do aviso do sino (vence em até 10 dias ou venceu, sem boleto);
 *  - enviar: boleto emitido que ainda não foi para o associado (qualquer vencimento);
 *  - lembrar: enviado, vence em até 10 dias — hora de lembrar o associado;
 *  - vencidas: enviado e vencido sem pagamento.
 * Parcela somada no boleto mensal fica de fora: ela segue o envio da fatura do mês.
 */
export function montarAcompanhamento(
  linhas: LinhaBanco[],
  hoje: string,
): Record<GrupoAcompanhamento, LinhaAcompanhamento[]> {
  const comEtapa = (l: LinhaParaEmitir, original: LinhaBanco): LinhaAcompanhamento => ({
    ...l,
    etapa: etapaBoletoParcela(original, original.fatura, original.parcelamento_id, hoje),
    mes_referencia: original.mes_referencia ?? null,
  });
  const porId = new Map(linhas.map((l) => [l.id, l]));
  const emitir = selecionarParaEmitir(linhas, hoje).map((l) => comEtapa(l, porId.get(l.parcela_id)!));

  const enviar: LinhaAcompanhamento[] = [];
  const lembrar: LinhaAcompanhamento[] = [];
  const vencidas: LinhaAcompanhamento[] = [];
  for (const l of linhas) {
    if (!l.fatura_id || !l.data_vencimento || !ACORDO_VALE.has(String(l.acordo?.status || ''))) continue;
    const etapa = etapaBoletoParcela(l, l.fatura, l.parcelamento_id, hoje);
    if (etapa !== 'a_enviar' && etapa !== 'enviada' && etapa !== 'vencida') continue;
    const dias = diasEntre(hoje, l.data_vencimento);
    const linha: LinhaAcompanhamento = {
      parcela_id: l.id,
      parcelamento_id: l.parcelamento_id,
      numero_parcela: l.numero_parcela,
      data_vencimento: l.data_vencimento,
      valor: Number(l.valor || 0),
      fatura_id: Number(l.fatura_id),
      cliente: String(l.acordo?.cliente_nome_snapshot || ''),
      uc: String(l.acordo?.uc_codigo_snapshot || ''),
      criado_por: l.acordo?.criado_por ?? null,
      dias_para_vencer: dias,
      etapa,
      mes_referencia: l.mes_referencia ?? null,
    };
    if (etapa === 'a_enviar') enviar.push(linha);
    else if (etapa === 'vencida') vencidas.push(linha);
    else if (dias <= DIAS_LEMBRAR) lembrar.push(linha);
  }
  const porUrgencia = (a: LinhaAcompanhamento, b: LinhaAcompanhamento) => a.dias_para_vencer - b.dias_para_vencer;
  return { emitir, enviar: enviar.sort(porUrgencia), lembrar: lembrar.sort(porUrgencia), vencidas: vencidas.sort(porUrgencia) };
}

/** Select do PostgREST para `montarAcompanhamento` (o do sino + o que diz a etapa). */
export const SELECT_ACOMPANHAMENTO =
  'id, parcelamento_id, numero_parcela, mes_referencia, data_vencimento, valor, status, tipo, fatura_id, '
  + 'acordo:parcelamentos!inner(status, cliente_nome_snapshot, uc_codigo_snapshot, criado_por), '
  + 'fatura:faturas!parcelamento_parcelas_fatura_id_fkey(num_cobranca_boleto, status_fatura, status_boleto_vigor, '
  + 'vencimento_boleto, data_pgto_boleto, quitado_em, parcelamento_origem_id)';
